<template>
  <div id="colorPicker" class="flex flex-col items-start gap-2 self-stretch">
    <UITextSmMedium
      v-if="typography === 'UITextSmMedium'"
      class="full-opacity text-gray-700"
      >{{ label }}
    </UITextSmMedium>
    <UITextXsMedium v-else>{{ label }} </UITextXsMedium>
    <div class="color-picker-container" :class="flatRadius ? 'left-zero' : ''">
      <div class="color-picker" :class="compact ? 'compact' : ''">
        <HLContentWrap class="hl-color-picker-wrap">
          <HLColorPicker
            id="basic-color-picker"
            :value="defaultValue"
            :swatches="swatches"
            :forward-picker-updates="true"
            placement="left-end"
            :content-style="COLOR_PICKER_POPOVER_STYLE"
            @swatch:add="handleSwatchAdd"
            @swatch:edit="handleSwatchEdit"
            @swatch:delete="handleSwatchDelete"
            @update:picker="handlePickerUpdate"
            @update:picker:back="handlePickerBack"
            @update:picker:close="handlePickerClose"
            @select="handleSelect"
          >
            <template v-if="swatchSize" #trigger>
              <HLSwatchTile
                :size="swatchSize"
                shape="square"
                :tabindex="0"
                :value="defaultValue"
                :transparent="defaultValue === 'transparent'"
              />
            </template>
            <template #swatchAddButton="{ sectionKey, sectionIndex }">
              <HLButton
                v-if="sectionKey === 'custom'"
                :id="`${sectionKey}-${sectionIndex}`"
                color="blue"
                text
                variant="secondary"
                class="p-0"
                @click="onSwatchAdd"
              >
                <Settings01Icon class="mr-1 h-3 w-3" />
                {{ $t("common.manage") }}
              </HLButton>
            </template></HLColorPicker
          ></HLContentWrap
        >
        <div class="color-display">
          <UITextSmNormal v-if="isDefaultState" class="default-text">{{
            t("common.default")
          }}</UITextSmNormal>
          <UITextSmNormal v-else>#{{ displayColor }}</UITextSmNormal>
        </div>
        <button
          v-if="clearable && !isDefaultState"
          class="clear-button absolute-clear"
          type="button"
          @click="handleClearColor"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 3L3 9M3 3L9 9"
              stroke="#667085"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useColorPickerSwatchCrudHandlers } from "@/composition/useColorPickerSwatchCrudHandlers";
import { useColorPickerSwatches } from "@/composition/useColorPickerSwatches";
import { useColorPickerValueHandlers } from "@/composition/useColorPickerValueHandlers";
import { useColorResolver } from "@/composition/useColorResolver";
import { useAppStore } from "@/store/app";
import {
  normalizeBrandboardsKey,
  toUpperHex,
  toUpperHexWithHash,
} from "@/util/color";
import { COLOR_PICKER_POPOVER_STYLE } from "@/util/colorPicker";
import type { ColorPickerSelectPayload } from "@/util/colorPicker";
import { Settings01Icon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UITextSmMedium,
  UITextSmNormal,
  UITextXsMedium,
} from "@gohighlevel/ghl-ui";
import type { HLSwatchTileSize } from "@platform-ui/highrise";
import {
  HLButton,
  HLColorPicker,
  HLContentWrap,
  HLSwatchTile,
} from "@platform-ui/highrise";
import type { PropType } from "vue";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  label: {
    type: String,
    default: "",
  },
  color: {
    type: String,
    default: "FFFFFF",
  },
  compact: {
    type: Boolean,
    default: false,
  },
  flatRadius: {
    type: Boolean,
    default: false,
  },
  typography: {
    type: String,
    default: "UITextXsMedium",
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  /* when true, renders the built-in transparent tile in the default colors section */
  showTransparent: {
    type: Boolean,
    default: false,
  },
  /* trigger-only swatch size override; undefined keeps the HLColorPicker default trigger */
  swatchSize: {
    type: String as PropType<HLSwatchTileSize | undefined>,
    default: undefined,
  },
});

const emit = defineEmits(["update:color"]);

const store = useAppStore();
const { isHandlebarsKey, resolveHexFromProp } = useColorResolver(store);

const TRANSPARENT_VALUE = "transparent";
const isEmptyColor = (value?: unknown): boolean =>
  !String(value ?? "").trim();

// When the transparent option is enabled, an empty color is rendered as the
// transparent preview; otherwise it falls back to the resolved hex (white).
const resolveDisplayValue = (value?: string): string =>
  props.showTransparent && isEmptyColor(value)
    ? TRANSPARENT_VALUE
    : toUpperHexWithHash(resolveHexFromProp(value));

const defaultValue = ref<string>(resolveDisplayValue(props.color));

const isDefaultState = computed(() => {
  const raw = String(props.color ?? "").trim();
  if (!raw) {
    return true;
  }
  if (isHandlebarsKey(raw) || normalizeBrandboardsKey(raw)) {
    return false;
  }
  return raw.replace(/^#/, "").toUpperCase() === "FFFFFF";
});

const displayColor = computed(() => toUpperHex(defaultValue.value));

const applyColorValue = (colorValue: string) => {
  defaultValue.value = toUpperHexWithHash(colorValue);
  emit("update:color", toUpperHex(colorValue));
};

// Clearing the color and selecting the built-in transparent tile share the
// same behavior: store an empty color. The preview shows transparent when the
// transparent option is enabled, otherwise it falls back to white.
const handleClearColor = () => {
  defaultValue.value = resolveDisplayValue("");
  emit("update:color", "");
};

const { swatches, onSwatchAdd, handleDefaultSwatchAdd } =
  useColorPickerSwatches(store, {
    t,
    onApplyColor: applyColorValue,
    showTransparent: props.showTransparent,
  });

const {
  handleSelect: handleColorSelect,
  handlePickerUpdate,
  handlePickerBack,
  handlePickerClose,
} = useColorPickerValueHandlers({
  getCommittedColor: () => resolveHexFromProp(props.color),
  onColor: applyColorValue,
});

// HLColorPicker emits the built-in transparent tile as a swatch select with
// tileIndex -1 and an undefined swatch payload, which the shared handler skips
// (getColorPickerSelectValue returns undefined). Treat that specific case
// exactly like clear; delegate every real swatch to the shared handler.
const handleSelect = (
  swatch: ColorPickerSelectPayload,
  _sectionKey?: string,
  _sectionIndex?: number,
  tileIndex?: number
) => {
  if (tileIndex === -1 || swatch == null) {
    handleClearColor();
    return;
  }
  handleColorSelect(swatch);
};

const { handleSwatchAdd, handleSwatchEdit, handleSwatchDelete } =
  useColorPickerSwatchCrudHandlers(store, {
    onCustomAdd: onSwatchAdd,
    onDefaultAdd: handleDefaultSwatchAdd,
    logPrefix: "ColorInput",
  });

watch(
  () => props.color,
  (newColor) => {
    defaultValue.value = resolveDisplayValue(newColor);
  }
);

watch(
  () => store.customColors,
  () => {
    if (isHandlebarsKey(props.color)) {
      defaultValue.value = resolveHexFromProp(props.color);
    }
  }
);
</script>
<style scoped>
.color-picker-container {
  position: relative;
  display: flex;
  padding: 7px 12px;
  align-items: center;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #ffffff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
}

.color-picker {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
}
.color-display {
  display: flex;
  align-items: center;
  flex: 1;
}

.default-text {
  color: #667085;
}

.clear-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  border-radius: 2px;
  transition: background-color 0.2s;
}

.clear-button:hover {
  background-color: #f2f4f7;
}

.absolute-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}
.hr-wrapper-container:not(.full-screen) {
  padding: 0px;
}

.compact {
  gap: 8px;
}

.v-binder-follower-content {
  z-index: 11000 !important;
}

.left-zero {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left-width: 0px !important;
}

.full-opacity {
  --tw-text-opacity: 1;
}
</style>
